'use client';

import { copyToClipboard } from '@/util/copy-to-clipboard';
import dayjs from 'dayjs';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ShareBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  shareUrl: string;
  onDownloadImage: () => void;
  onCopyLink: () => void;
  personaLabel?: string;
  isLoadingShareUrl?: boolean;
}

export function ShareBottomSheet({
  isOpen,
  onClose,
  imageUrl,
  shareUrl,
  onDownloadImage,
  onCopyLink,
  personaLabel = 'Health Avenger',
  isLoadingShareUrl = false,
}: ShareBottomSheetProps) {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [supportsWebShare, setSupportsWebShare] = useState(false);
  const [linePluginLoaded, setLinePluginLoaded] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSupportsWebShare(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  useEffect(() => {
    if (imageUrl) {
      const img = new Image();
      img.onload = () => setIsImageLoaded(true);
      img.onerror = () => setIsImageLoaded(false);
      img.src = imageUrl;
    }
  }, [imageUrl]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setDragY(0);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleDragStart = (clientY: number) => {
    dragStartY.current = clientY;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const delta = y - dragStartY.current!;
      if (delta > 0) setDragY(delta);
    };

    const onEnd = () => {
      setDragY((current) => {
        if (current > 100) {
          setTimeout(() => handleClose(), 0);
          return current;
        }
        return 0;
      });
      dragStartY.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
  };

  const handleSocialImageShare = async (platform: 'Instagram' | 'TikTok') => {
    if (isLoadingShareUrl) {
      alert(t('share.preparing_link'));
      return;
    }

    if (!shareUrl) {
      alert(t('share.error_no_link'));
      return;
    }

    console.log(`📱 ${platform} share clicked, supportsWebShare:`, supportsWebShare);

    if (supportsWebShare && navigator.share) {
      try {
        console.log(`📸 Fetching image for ${platform} share...`);
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        const fileName = `${personaLabel} Persona.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        
        const shareText = `ดูผลลัพธ์แบบสำรวจของฉัน\n\n${shareUrl}`;

        console.log(`✨ Sharing to ${platform} via Web Share API with filename:`, fileName);
        await navigator.share({
          title: `${personaLabel} Persona`,
          text: shareText,
          files: [file],
        });

        console.log(`✅ ${platform} share completed`);
        handleClose();
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log(`❌ ${platform} share cancelled by user`);
          return;
        }
        console.error(`❌ ${platform} Web Share failed, falling back to copy URL:`, error);
        await handleCopyUrl();
      }
    } else {
      console.log(`💻 ${platform} - copying share URL (desktop)`);
      await handleCopyUrl();
    }
  };



  const handleCopyUrl = async () => {
    if (!shareUrl) {
      alert(t('share.error_no_link'));
      return;
    }

    try {
      await copyToClipboard(shareUrl);
      alert(t('share.copied_link'));
      handleClose();
    } catch (error) {
      console.error('Failed to copy URL:', error);
      onCopyLink(); 
    }
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transition-transform ease-out ${
          dragY > 0 ? 'duration-0' : 'duration-300'
        } ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        <div
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none select-none"
          onMouseDown={(e) => handleDragStart(e.clientY)}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
        >
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="px-6 pb-safe-or-8 max-h-[85vh] overflow-y-auto">
          {/* Preview Image - Stories Format */}
          <div className="mb-6 flex flex-col items-center">
            <div className="text-center text-base font-semibold text-gray-800 mb-4">
              แชร์ผลลัพธ์ของคุณ
            </div>
            <div className="relative w-full max-w-[280px] aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-gray-100">
              {!isImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-gray-600"></div>
                </div>
              )}
              <img
                src={imageUrl}
                alt="Share preview"
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  isImageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setIsImageLoaded(true)}
              />
            </div>
          </div>

          {/* Social Media Icons Row */}
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-3 text-center">
              แชร์ไปยัง
            </div>
            <div className="flex items-center justify-center gap-6 px-4">
              {/* Instagram */}
              <button
                onClick={() => handleSocialImageShare('Instagram')}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <img
                    src="/assets/icons/instagram.svg"
                    alt="Instagram"
                    width={28}
                    height={28}
                    className="brightness-0 invert"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  />
                </div>
                <span className="text-xs text-gray-600">Instagram</span>
              </button>

              {/* TikTok */}
              <button
                onClick={() => handleSocialImageShare('TikTok')}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <img
                    src="/assets/icons/tiktok.svg"
                    alt="TikTok"
                    width={28}
                    height={28}
                    className="brightness-0 invert"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  />
                </div>
                <span className="text-xs text-gray-600">TikTok</span>
              </button>

              <button
                onClick={() => {
                  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${shareUrl}?t=${dayjs().unix()}`)}`;
                  window.open(fbUrl, '_blank', 'width=600,height=400');
                  handleClose();
                }}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#1877F2] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <img
                    src="/assets/icons/facebook.svg"
                    alt="Facebook"
                    width={28}
                    height={28}
                    className="brightness-0 invert"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  />
                </div>
                <span className="text-xs text-gray-600">Facebook</span>
              </button>

              <button
                onClick={() => {
                  const lineUrl = `https://line.me/R/share?text=${encodeURIComponent(`${shareUrl}?t=${dayjs().unix()}`)}`;
                  if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
                    window.location.href = lineUrl;
                  } else {
                    window.open(lineUrl, '_blank', 'width=600,height=600');
                  }
                  handleClose();
                }}
                className="flex flex-col items-center gap-2 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#00B900] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <img
                    src="/assets/icons/line.svg"
                    alt="LINE"
                    width={28}
                    height={28}
                    className="brightness-0 invert"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  />
                </div>
                <span className="text-xs text-gray-600">LINE</span>
              </button>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-gray-200">
            <button
              onClick={onDownloadImage}
              className="w-full flex items-center justify-center gap-3 py-4 px-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="font-medium">บันทึกรูปภาพ</span>
            </button>

            <button
              onClick={onCopyLink}
              className="w-full flex items-center justify-center gap-3 py-4 px-4 bg-white border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="font-medium">คัดลอกลิงก์</span>
            </button>
          </div>
        </div>
      </div>

      <Script
        src="https://www.line-website.com/social-plugins/js/thirdparty/loader.min.js"
        strategy="lazyOnload"
        onLoad={() => setLinePluginLoaded(true)}
      />
    </>
  );
}
